import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { MetaLabel } from "../components/Bits";
import { setSeo } from "../lib/seo";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ToolResidence() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | ok | denied
  const [data, setData] = useState(null);

  useEffect(() => { setSeo({ title: "The Residence Service Record · My Library" }); }, []);
  useEffect(() => {
    if (!user) { nav("/login", { state: { from: "/tool/residence-service-record" } }); return; }
    api.get("/rsr/library").then((r) => { setData(r.data); setState("ok"); }).catch(() => setState("denied"));
  }, [user]); // eslint-disable-line

  const open = async (slug, dl) => {
    try {
      const { data } = await api.post("/rsr/file-ticket", { slug });
      const url = `${BACKEND}/api/rsr/file/${slug}?ticket=${encodeURIComponent(data.ticket)}${dl ? "&dl=1" : ""}`;
      window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (state === "loading") return <div className="min-h-screen pt-32 text-center text-sm text-ink-soft">Loading…</div>;
  if (state === "denied") return (
    <div className="min-h-screen pt-32 text-center px-6" data-testid="rsr-denied">
      <Lock size={22} className="mx-auto text-accent" />
      <h1 className="mt-6 font-serif font-light text-3xl text-ink">This tool isn't in your library yet.</h1>
      <Link to="/salotto-digitale/residence-service-record" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] link-underline">View The Residence Service Record</Link>
    </div>
  );

  return (
    <div data-testid="rsr-tool-page" className="pt-24 min-h-screen">
      <div className="mx-auto max-w-[1000px] px-6 lg:px-12 py-12">
        <MetaLabel>Use · Professional Tool</MetaLabel>
        <h1 className="mt-6 font-serif font-light text-4xl text-ink">The Residence Service Record</h1>
        <p className="mt-4 text-base font-light text-ink-soft max-w-2xl">Your professional tool is ready. Begin with the Quick Start Guide before setting up your first residence, then keep the workbook as the operational history of the residence.</p>

        <div className="mt-12 space-y-4">
          {(data?.files || []).map((f) => (
            <div key={f.slug} data-testid={`rsr-file-${f.slug}`} className="flex items-center justify-between gap-6 border border-line p-6 flex-wrap">
              <div className="flex items-start gap-4">
                <FileText size={20} className="mt-1 text-accent shrink-0" />
                <div>
                  <p className="font-serif text-lg text-ink">{f.title}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.15em] text-ink-soft">{f.kind === "pdf" ? "PDF Guide" : "Excel Workbook"}{f.filename ? ` · ${f.filename}` : ""}</p>
                </div>
              </div>
              <div className="flex gap-3">
                {f.kind === "pdf" && (
                  <button onClick={() => open(f.slug, 0)} data-testid={`rsr-view-${f.slug}`} className="text-[11px] uppercase tracking-[0.2em] text-ink link-underline">View</button>
                )}
                <button onClick={() => open(f.slug, 1)} data-testid={`rsr-download-${f.slug}`} className="inline-flex items-center gap-2 bg-ink text-bg px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors">
                  <Download size={14} /> Download
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-xs font-light text-ink-soft max-w-2xl leading-relaxed">This workbook does not send reminders or emails, contact vendors, or connect to AI. Your calendar remains the reminder mechanism. Before using any external AI tool, remove client names, addresses, access, financial and security details.</p>
        <Link to="/salotto-digitale" className="mt-8 inline-block text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline">Return to Il Salotto Digitale</Link>
      </div>
    </div>
  );
}
